import { escapeCSS } from '@doltech/utils/lib/object';
import { noop } from 'lodash';
import { Editor, Range, Transforms } from 'slate';
import { ReactEditor } from 'slate-react';

import { MediaType } from '../../DolEditor/elements/media-constant';
import { EX } from '../../DolEditor/elements/vocab-card/type';
import { VOCAB_CARD } from './CardLogic/model/constants';
import { ELEMENT_QUOTE } from './Quote/QuotePlugin.types';
import { ELEMENT_IELTS_CALENDAR_COMPUTER_BASED } from './SpecialObject/IeltsCalendar/ComputerBased/IeltsCalendarComputerBasedPlugin.types';
import { ELEMENT_IELTS_PURPOSE_CONVERTER } from './SpecialObject/IeltsConverter/Purpose/IeltsPurposeConverterPlugin.types';
import { SAMPLE_SPECIAL_OBJECT_VOID } from './SpecialObject/IeltsSample/types';
import { ELEMENT_IMAGE_SLIDE_SHOW } from './SpecialObject/ImageSlide/SlideShow/ImageSlideShowPlugin.types';
import { ELEMENT_IMAGE_SLIDE_TEXT } from './SpecialObject/ImageSlide/SlideText/ImageSlideTextPlugin.types';
import { SPECIAL_OBJECT_VOID } from './SpecialObject/types';

const autoFocusFirstInput = ({ el }) => {
  const inputEl = el.querySelector('input');
  if (inputEl) {
    inputEl.focus();
  }
};

const ELEMENT_TO_FOCUS_ON_DELETE_END = [
  {
    type: MediaType.IMAGE,
    selector: (id) => `#${id}`,
    foundElm: ({ el }) => {
      const inputEl = el.querySelector('.caption-of-picture');
      if (inputEl) {
        setTimeout(() => {
          inputEl.focus();
        }, 0);
      }
    },
  },
  {
    type: MediaType.VOCAB_LIST_NEWS,
    selector: (id) => `#${id}`,
    foundElm: autoFocusFirstInput,
  },
  {
    type: MediaType.VIDEO,
    selector: (id) => `#${id}`,
    foundElm: autoFocusFirstInput,
  },
  {
    type: MediaType.FILE,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: MediaType.AUDIO,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: MediaType.CALCULATOR,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: MediaType.CONVERTER,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: MediaType.PAPER_EXAM,
    selector: (id) => `#${id}`,
    foundElm: autoFocusFirstInput,
  },
  {
    type: SPECIAL_OBJECT_VOID,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: SAMPLE_SPECIAL_OBJECT_VOID,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: ELEMENT_IELTS_CALENDAR_COMPUTER_BASED,
    selector: (id) => `#${id}`,
    foundElm: autoFocusFirstInput,
  },
  {
    type: ELEMENT_IELTS_PURPOSE_CONVERTER,
    selector: (id) => `#${id}`,
    foundElm: autoFocusFirstInput,
  },
  {
    type: ELEMENT_IMAGE_SLIDE_SHOW,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: ELEMENT_IMAGE_SLIDE_TEXT,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: ELEMENT_QUOTE,
    selector: (id) => `#${id}`,
    foundElm: noop,
  },
  {
    type: VOCAB_CARD,
    selector: (id) => `#${id}`,
    foundElm: ({ editor, beforeNode }) => {
      const [node] = Editor.nodes(editor, {
        at: [],
        match: (n: any) => {
          return n?.id === beforeNode.id && n.type === EX;
        },
      });
      const [, path] = node;
      ReactEditor.focus(editor);
      Transforms.select(editor, path);
    },
  },
];

export const withDeleteAtEndNode =
  () =>
  <T extends ReactEditor>(editor: T) => {
    const { deleteBackward } = editor;

    editor.deleteBackward = (unit) => {
      if (editor.selection) {
        const isCollapse = Range.isCollapsed(editor.selection);
        if (isCollapse) {
          const beforePath = Editor.before(editor, editor.selection, { unit });
          const allTypes = ELEMENT_TO_FOCUS_ON_DELETE_END.map((item) => item.type);
          const [beforeNodeEntries] = Editor.nodes(editor, {
            at: beforePath,
            match: (n: any) => allTypes.includes(n?.type),
          });
          // delete at end of node

          if (beforeNodeEntries) {
            const [node] = Editor.nodes(editor, {
              at: editor.selection,
              match: (n: any) => allTypes.includes(n?.type),
            });
            if (node) {
              deleteBackward(unit);
              return;
            }

            const [beforeNode, beforeNodePath] = beforeNodeEntries as any;
            Transforms.select(editor, beforePath);

            const elementToFocus = ELEMENT_TO_FOCUS_ON_DELETE_END.find(
              (item) => item.type === beforeNode.type
            );
            const el = document.querySelector(
              `${elementToFocus.selector(escapeCSS(beforeNode.id))}`
            ) as any;
            if (el) {
              el.click();
              elementToFocus.foundElm({ editor, el, beforePath: beforeNodePath, beforeNode });
            }

            return;
          }
        }
      }
      deleteBackward(unit);
    };

    return editor;
  };
